import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import type { WebEvent } from '../types/events';
import type { GalleryCursor, WebMedia } from '../types/media';
import type { PreparedWebPhoto, WebPendingUpload } from '../types/uploads';
import { eventAllowsUploads, getEventLifecycleLabel } from '../domain/eventLifecycle';
import {
  loadGalleryPage,
  loadSignedVideoDelivery,
  subscribeToGalleryChanges,
} from '../services/gallery';
import {
  MAX_WEB_PHOTO_BATCH,
  finalizeWebPhotoUpload,
  prepareWebPhoto,
  putWebPhotoToR2,
  reserveWebPhotoUploads,
} from '../services/uploads';
import { EventManagementPanel } from './EventManagementPanel';
import { HostControlsPanel } from './HostControlsPanel';
import { PeoplePanel } from './PeoplePanel';
import { PurchasePanel } from './PurchasePanel';

type WorkspaceSection = 'gallery' | 'people' | 'management' | 'purchases' | 'host';

type EventWorkspaceProps = {
  event: WebEvent;
  session: Session;
  authBusy: boolean;
  initialSection?: WorkspaceSection;
  onBack: () => void;
  onSignOut: () => Promise<void>;
  onEventUpdated: (event: WebEvent) => void;
  onEventDeleted: (eventId: string) => void;
};

function formatMediaDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatEventDate(event: WebEvent): string {
  const value = event.start_time || event.event_date || event.created_at;
  if (!value) return 'Date TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date TBD';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function uploaderName(media: WebMedia): string {
  return media.uploader?.full_name?.trim() || 'Gathrd guest';
}

function mediaGridUrl(media: WebMedia): string | null {
  return media.media_type === 'video'
    ? media.delivery?.poster ?? null
    : media.delivery?.thumb ?? media.delivery?.display ?? null;
}

function mediaViewerUrl(media: WebMedia): string | null {
  return media.media_type === 'video'
    ? media.delivery?.playback ?? null
    : media.delivery?.full ?? media.delivery?.display ?? media.delivery?.thumb ?? null;
}

function uploadStageLabel(upload: WebPendingUpload): string {
  switch (upload.stage) {
    case 'signing': return 'Preparing';
    case 'uploading': return `Uploading ${upload.progress}%`;
    case 'finalizing': return 'Finishing';
    case 'reviewing': return 'Reviewing';
    case 'blocked': return 'Not published';
    case 'failed': return 'Upload failed';
    default: return 'Working';
  }
}

export function EventWorkspace({
  event,
  session,
  authBusy,
  initialSection = 'gallery',
  onBack,
  onSignOut,
  onEventUpdated,
  onEventDeleted,
}: EventWorkspaceProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const galleryRefreshRunningRef = useRef(false);
  const galleryRefreshQueuedRef = useRef(false);
  const [section, setSection] = useState<WorkspaceSection>(initialSection);
  const [galleryMedia, setGalleryMedia] = useState<WebMedia[]>([]);
  const [galleryCursor, setGalleryCursor] = useState<GalleryCursor | null>(null);
  const [galleryHasMore, setGalleryHasMore] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<WebMedia | null>(null);
  const [videoDeliveryBusyId, setVideoDeliveryBusyId] = useState<string | null>(null);
  const [pendingUploads, setPendingUploads] = useState<WebPendingUpload[]>([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshGallery = useCallback(async () => {
    if (galleryRefreshRunningRef.current) {
      galleryRefreshQueuedRef.current = true;
      return;
    }

    galleryRefreshRunningRef.current = true;
    setLoadingGallery(true);
    setError(null);
    try {
      do {
        galleryRefreshQueuedRef.current = false;
        const page = await loadGalleryPage(event.id);
        setGalleryMedia(page.items);
        setGalleryCursor(page.nextCursor);
        setGalleryHasMore(page.hasMore);
      } while (galleryRefreshQueuedRef.current);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load this gallery.');
    } finally {
      galleryRefreshRunningRef.current = false;
      setLoadingGallery(false);
    }
  }, [event.id]);

  const scheduleGalleryRefresh = useCallback(() => {
    if (galleryRefreshTimerRef.current) clearTimeout(galleryRefreshTimerRef.current);
    galleryRefreshTimerRef.current = setTimeout(() => {
      galleryRefreshTimerRef.current = null;
      void refreshGallery();
    }, 300);
  }, [refreshGallery]);

  useEffect(() => {
    void refreshGallery();
    const unsubscribe = subscribeToGalleryChanges(event.id, (row) => {
      const localClientId = String(row?.local_client_id ?? '').trim();
      const moderationStatus = String(row?.moderation_status ?? '').trim().toLowerCase();
      if (localClientId && ['quarantined', 'rejected'].includes(moderationStatus)) {
        setPendingUploads((current) =>
          current.map((upload) =>
            upload.localClientId === localClientId
              ? {
                  ...upload,
                  stage: 'blocked',
                  moderationStatus,
                  error: 'Gathrd moderation did not publish this photo.',
                }
              : upload,
          ),
        );
      }
      scheduleGalleryRefresh();
    });

    return () => {
      unsubscribe();
      if (galleryRefreshTimerRef.current) {
        clearTimeout(galleryRefreshTimerRef.current);
        galleryRefreshTimerRef.current = null;
      }
      galleryRefreshQueuedRef.current = false;
    };
  }, [event.id, refreshGallery, scheduleGalleryRefresh]);

  useEffect(() => {
    const publishedLocalIds = new Set(
      galleryMedia.map((media) => String(media.local_client_id ?? '').trim()).filter(Boolean),
    );
    if (publishedLocalIds.size === 0) return;
    setPendingUploads((current) =>
      current.filter((upload) => {
        if (!publishedLocalIds.has(upload.localClientId)) return true;
        URL.revokeObjectURL(upload.previewUrl);
        return false;
      }),
    );
  }, [galleryMedia]);

  function clearPendingUploads() {
    setPendingUploads((current) => {
      current.forEach((upload) => URL.revokeObjectURL(upload.previewUrl));
      return [];
    });
  }

  function updatePendingUpload(localClientId: string, patch: Partial<WebPendingUpload>) {
    setPendingUploads((current) =>
      current.map((upload) =>
        upload.localClientId === localClientId ? { ...upload, ...patch } : upload,
      ),
    );
  }

  function dismissPendingUpload(localClientId: string) {
    setPendingUploads((current) =>
      current.filter((upload) => {
        if (upload.localClientId !== localClientId) return true;
        URL.revokeObjectURL(upload.previewUrl);
        return false;
      }),
    );
  }

  async function openMedia(media: WebMedia) {
    setSelectedMedia(media);
    if (media.media_type !== 'video' || media.delivery?.playback) return;

    setVideoDeliveryBusyId(media.id);
    try {
      const hydrated = await loadSignedVideoDelivery(media);
      setSelectedMedia((current) => (current?.id === media.id ? hydrated : current));
    } catch (err) {
      console.warn(
        '[web gallery] Signed Stream delivery failed:',
        err instanceof Error ? err.message : err,
      );
    } finally {
      setVideoDeliveryBusyId((current) => (current === media.id ? null : current));
    }
  }

  async function loadMoreGalleryMedia() {
    if (!galleryCursor || !galleryHasMore || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await loadGalleryPage(event.id, galleryCursor);
      setGalleryMedia((current) => {
        const byId = new Map(current.map((item) => [item.id, item]));
        for (const item of page.items) byId.set(item.id, item);
        return [...byId.values()];
      });
      setGalleryCursor(page.nextCursor);
      setGalleryHasMore(page.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load more media.');
    } finally {
      setLoadingMore(false);
    }
  }

  async function uploadPreparedPhoto(
    photo: PreparedWebPhoto,
    reservationMap: Awaited<ReturnType<typeof reserveWebPhotoUploads>>,
  ) {
    const reservation = reservationMap.get(photo.localClientId);
    if (!reservation) {
      updatePendingUpload(photo.localClientId, {
        stage: 'failed',
        error: 'Gathrd did not return an upload reservation for this photo.',
      });
      return;
    }

    try {
      updatePendingUpload(photo.localClientId, {
        stage: 'uploading',
        progress: 0,
        photoId: reservation.photoId,
      });
      const etag = await putWebPhotoToR2(photo, reservation, (progress) => {
        updatePendingUpload(photo.localClientId, { progress });
      });
      updatePendingUpload(photo.localClientId, { stage: 'finalizing', progress: 100 });
      const finalized = await finalizeWebPhotoUpload(photo, reservation, etag);
      updatePendingUpload(photo.localClientId, {
        stage: 'reviewing',
        photoId: finalized.photoId,
        moderationStatus: finalized.moderationStatus,
        error: null,
      });
    } catch (err) {
      updatePendingUpload(photo.localClientId, {
        stage: 'failed',
        error: err instanceof Error ? err.message : 'Photo upload failed.',
      });
    }
  }

  async function handlePhotoSelection(changeEvent: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(changeEvent.currentTarget.files ?? []);
    changeEvent.currentTarget.value = '';
    if (files.length === 0 || uploadBusy) return;
    if (!eventAllowsUploads(event)) {
      setError('Uploads are not available for this event.');
      return;
    }
    if (files.length > MAX_WEB_PHOTO_BATCH) {
      setError(`Choose up to ${MAX_WEB_PHOTO_BATCH} photos at a time on the web.`);
      return;
    }

    setUploadBusy(true);
    setError(null);
    const prepared: PreparedWebPhoto[] = [];
    const staged: WebPendingUpload[] = [];
    try {
      for (const file of files) {
        const photo = await prepareWebPhoto(file);
        prepared.push(photo);
        staged.push({
          localClientId: photo.localClientId,
          fileName: photo.fileName,
          previewUrl: URL.createObjectURL(file),
          stage: 'signing',
          progress: 0,
          photoId: null,
          moderationStatus: null,
          error: null,
        });
      }
      setPendingUploads((current) => [...staged, ...current]);
      const reservations = await reserveWebPhotoUploads(event.id, prepared);
      let nextIndex = 0;
      const workerCount = Math.min(3, prepared.length);
      await Promise.all(
        Array.from({ length: workerCount }, async () => {
          while (nextIndex < prepared.length) {
            const index = nextIndex;
            nextIndex += 1;
            await uploadPreparedPhoto(prepared[index], reservations);
          }
        }),
      );
      scheduleGalleryRefresh();
    } catch (err) {
      if (staged.length === 0) {
        setError(err instanceof Error ? err.message : 'Unable to prepare these photos.');
      } else {
        const message = err instanceof Error ? err.message : 'Unable to start these uploads.';
        setPendingUploads((current) =>
          current.map((upload) =>
            staged.some((item) => item.localClientId === upload.localClientId)
              ? { ...upload, stage: 'failed', error: message }
              : upload,
          ),
        );
      }
    } finally {
      setUploadBusy(false);
    }
  }

  async function copyEventCode() {
    const code = String(event.code ?? '').trim();
    if (!code || !navigator.clipboard) return;
    await navigator.clipboard.writeText(code);
    setCodeCopied(true);
    window.setTimeout(() => setCodeCopied(false), 1000);
  }

  const photoCount = galleryMedia.filter((item) => item.media_type !== 'video').length;
  const videoCount = galleryMedia.filter((item) => item.media_type === 'video').length;
  const canUpload = eventAllowsUploads(event);
  const showHostControls = event.access_role === 'organizer' || event.access_role === 'co_admin';
  const cover = event.cover_photo_url || event.preview_thumbnail_urls?.[0] || null;
  const heroStyle = cover
    ? { backgroundImage: `linear-gradient(135deg, rgba(15,23,42,.20), rgba(15,23,42,.58)), url("${cover}")` }
    : undefined;

  return (
    <div className="legacy-event-workspace">
      <section className="legacy-gallery-hero" style={heroStyle}>
        <div className="legacy-gallery-hero-copy">
          <button
            className="legacy-btn light"
            type="button"
            onClick={() => {
              clearPendingUploads();
              setSelectedMedia(null);
              onBack();
            }}
          >
            ← Back to events
          </button>
          <p className="eyebrow">Event Gallery</p>
          <h1>{event.name}</h1>
          <p>{event.description || 'A private shared album for this event.'}</p>
          <div className="legacy-gallery-meta">
            <span>Code {event.code || '—'}</span>
            <span>{photoCount + videoCount} memories</span>
            <span>{event.access_role.replace('_', ' ')}</span>
            <span>{getEventLifecycleLabel(event)}</span>
          </div>
        </div>
        <div className="legacy-gallery-actions">
          <button className="legacy-btn light" type="button" onClick={() => void copyEventCode()} disabled={!event.code}>{codeCopied ? 'Copied' : 'Copy Code'}</button>
          <button className="legacy-btn ghost-light" type="button" onClick={() => void refreshGallery()} disabled={loadingGallery}>{loadingGallery ? 'Refreshing…' : 'Refresh'}</button>
          <button className="legacy-btn ghost-light" type="button" onClick={() => void onSignOut()} disabled={authBusy}>Sign out</button>
        </div>
      </section>

      {error && <p className="error-banner">{error}</p>}

      <nav className="legacy-workspace-tabs" aria-label="Event workspace sections">
        <button className={section === 'gallery' ? 'active' : ''} type="button" onClick={() => setSection('gallery')}>Gallery</button>
        <button className={section === 'people' ? 'active' : ''} type="button" onClick={() => setSection('people')}>People & invites</button>
        {showHostControls && <button className={section === 'management' ? 'active' : ''} type="button" onClick={() => setSection('management')}>Management</button>}
        <button className={section === 'purchases' ? 'active' : ''} type="button" onClick={() => setSection('purchases')}>Purchases</button>
        {showHostControls && <button className={section === 'host' ? 'active' : ''} type="button" onClick={() => setSection('host')}>Host settings</button>}
      </nav>

      {section === 'people' ? (
        <section className="legacy-card legacy-panel-wrap">
          <PeoplePanel event={event} currentUserId={session.user.id} />
        </section>
      ) : section === 'management' && showHostControls ? (
        <section className="legacy-card legacy-panel-wrap">
          <EventManagementPanel
            event={event}
            currentUserId={session.user.id}
            media={galleryMedia}
            onEventUpdated={onEventUpdated}
            onGalleryChanged={scheduleGalleryRefresh}
          />
        </section>
      ) : section === 'purchases' ? (
        <section className="legacy-card legacy-panel-wrap">
          <PurchasePanel
            event={event}
            currentUserId={session.user.id}
            email={session.user.email}
          />
        </section>
      ) : section === 'host' && showHostControls ? (
        <section className="legacy-card legacy-panel-wrap">
          <HostControlsPanel event={event} onEventUpdated={onEventUpdated} onEventDeleted={onEventDeleted} />
        </section>
      ) : (
        <div className="legacy-gallery-layout">
          <section className="legacy-card legacy-gallery-card">
            <div className="legacy-gallery-toolbar">
              <div>
                <h2>Memories</h2>
                <p className="legacy-help">Photos and videos stay synchronized with the mobile event gallery.</p>
              </div>
              <button className="legacy-btn secondary" type="button" onClick={() => void refreshGallery()} disabled={loadingGallery}>{loadingGallery ? 'Refreshing…' : 'Refresh gallery'}</button>
            </div>

            <section className="gallery-summary" aria-label="Gallery publication status">
              <span className="lifecycle-pill">{getEventLifecycleLabel(event)}</span>
              <span>{canUpload ? `Add up to ${MAX_WEB_PHOTO_BATCH} JPEG, PNG, WebP, or HEIC photos at a time.` : 'This event is view-only; new uploads are disabled.'}</span>
            </section>

            {pendingUploads.length > 0 && (
              <section className="upload-tray" aria-label="Web photo uploads" aria-live="polite">
                <div className="upload-tray-heading"><div><strong>Web uploads</strong><span>Photos stay here while Gathrd uploads and reviews them.</span></div></div>
                <div className="upload-grid">
                  {pendingUploads.map((upload) => (
                    <article className={`upload-card upload-${upload.stage}`} key={upload.localClientId}>
                      <img src={upload.previewUrl} alt="Local upload preview" />
                      <div className="upload-card-body">
                        <strong title={upload.fileName}>{upload.fileName}</strong>
                        <span>{uploadStageLabel(upload)}</span>
                        {upload.stage === 'uploading' && <div className="upload-progress" aria-label={`${upload.progress}% uploaded`}><span style={{ width: `${upload.progress}%` }} /></div>}
                        {upload.error && <small>{upload.error}</small>}
                        {['failed', 'blocked'].includes(upload.stage) && <button type="button" className="text-button upload-dismiss" onClick={() => dismissPendingUpload(upload.localClientId)}>Dismiss</button>}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}

            <section className="media-grid" aria-live="polite">
              {!loadingGallery && galleryMedia.length === 0 ? (
                <div className="empty-state"><h2>No published media yet</h2><p>Uploads still processing or awaiting moderation remain hidden until Gathrd publishes them.</p></div>
              ) : (
                galleryMedia.map((media) => {
                  const gridUrl = mediaGridUrl(media);
                  return (
                    <button className="media-card" key={media.id} type="button" onClick={() => void openMedia(media)}>
                      <div className="media-frame">
                        {gridUrl ? <img src={gridUrl} alt={media.caption || `Media from ${uploaderName(media)}`} loading="lazy" /> : <div className="media-placeholder"><span>{media.media_type === 'video' ? '▶' : '◻'}</span><small>{media.media_type === 'video' ? 'Video processing' : 'Media unavailable'}</small></div>}
                        {media.media_type === 'video' && <span className="video-badge">▶ Video</span>}
                      </div>
                      <div className="media-meta"><strong>{uploaderName(media)}</strong><span>{formatMediaDate(media.created_at)}</span>{media.caption && <p>{media.caption}</p>}</div>
                    </button>
                  );
                })
              )}
            </section>

            {galleryHasMore && <div className="load-more-row"><button className="legacy-btn secondary" onClick={() => void loadMoreGalleryMedia()} disabled={loadingMore}>{loadingMore ? 'Loading…' : 'Load more'}</button></div>}
          </section>

          <aside className="legacy-side-panel">
            <section className="legacy-card legacy-upload-card">
              <div className="legacy-upload-icon">⇧</div>
              <h3>Upload from computer</h3>
              <p className="legacy-help">Choose photos here. They use Gathrd’s signed R2 upload, finalization, and moderation pipeline.</p>
              <input
                ref={fileInputRef}
                className="visually-hidden"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic"
                multiple
                onChange={(inputEvent) => void handlePhotoSelection(inputEvent)}
              />
              <button className="legacy-btn primary full" type="button" onClick={() => fileInputRef.current?.click()} disabled={!canUpload || uploadBusy}>{uploadBusy ? 'Uploading…' : 'Choose Photos'}</button>
              {!canUpload && <p className="error-banner">Uploads are locked, archived, or unavailable for this event.</p>}
            </section>

            <section className="legacy-card">
              <h3>Event details</h3>
              <div className="legacy-event-detail-list">
                <div><strong>Date</strong><span>{formatEventDate(event)}</span></div>
                <div><strong>Location</strong><span>{event.location_name || event.location_address || 'Not set'}</span></div>
                <div><strong>Event code</strong><span>{event.code || 'Not available'}</span></div>
                <div><strong>Access</strong><span>{event.access_role.replace('_', ' ')}</span></div>
              </div>
              <button className="legacy-btn secondary full" type="button" onClick={() => setSection('people')}>People & Invites</button>
              <button className="legacy-btn secondary full" type="button" onClick={() => setSection('purchases')} style={{ marginTop: 10 }}>Purchases</button>
              {showHostControls && <button className="legacy-btn secondary full" type="button" onClick={() => setSection('management')} style={{ marginTop: 10 }}>Manage Event</button>}
              {showHostControls && <button className="legacy-btn secondary full" type="button" onClick={() => setSection('host')} style={{ marginTop: 10 }}>Host Settings</button>}
            </section>
          </aside>
        </div>
      )}

      {selectedMedia && (
        <div className="viewer-overlay" role="dialog" aria-modal="true" aria-label="Gathrd media viewer">
          <div className="viewer-panel">
            <div className="viewer-toolbar">
              <div><strong>{uploaderName(selectedMedia)}</strong><span>{formatMediaDate(selectedMedia.created_at)}</span></div>
              <button className="viewer-close" type="button" onClick={() => setSelectedMedia(null)} aria-label="Close media viewer">×</button>
            </div>
            <div className="viewer-media">
              {selectedMedia.media_type === 'video' ? (
                videoDeliveryBusyId === selectedMedia.id ? (
                  <div className="viewer-unavailable">Preparing secure video playback…</div>
                ) : mediaViewerUrl(selectedMedia) ? (
                  <video controls poster={selectedMedia.delivery?.poster ?? undefined} src={mediaViewerUrl(selectedMedia) ?? undefined} />
                ) : (
                  <div className="viewer-unavailable">This video is still processing or signed delivery is temporarily unavailable.</div>
                )
              ) : mediaViewerUrl(selectedMedia) ? (
                <img src={mediaViewerUrl(selectedMedia) ?? undefined} alt={selectedMedia.caption || `Media from ${uploaderName(selectedMedia)}`} />
              ) : <div className="viewer-unavailable">This media item does not currently have a web-deliverable URL.</div>}
            </div>
            {selectedMedia.caption && <p className="viewer-caption">{selectedMedia.caption}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
